import {
    createElement,
    randomNumGenerator,
    replaceInString,
    validateNum,
} from "../../../core/js/utilities";
import { renderState } from "../../../core/js/state-machine";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { alertNotice } from "../../../core/js/helpers/built-in-icons";
import {
    schema,
    componentName,
    defaultProps,
    componentClasses,
    views,
} from "../../../core/components/text-area/text-area";

const validateHTML = (element) => {
    const textArea = element.querySelector(`.${componentClasses.basic}`);
    const header = element.querySelector(`.${componentClasses.header}`);
    return textArea ? { textArea, header } : false;
};

export default function TextArea(element, props) {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        schema,
        validateHTML,
    });
    if (!success) return;

    let counterSpan;
    let currentSpan;
    let srText;
    let inactivityTimer;
    let maxlengthTimer;
    let srCounterMessage;

    //Insert alert icon
    const injectErrorIcon = () => {
        validElements.textArea.insertAdjacentHTML(
            "afterend",
            alertNotice({ class: componentClasses.errorIcon })
        );
    };

    const maxLength = validateNum(validElements.textArea.dataset.maxlength)
        ? validElements.textArea.dataset.maxlength
        : mergedProps.maxCounterLength;

    // Sets the content of the span for screen reader to read aloud
    const srReadout = (message, politeness = "") => {
        srText.innerHTML = message;
        if (politeness) {
            srText.setAttribute("aria-live", politeness);
        }
    };

    // Sets the content of the span for screen reader to read aloud
    const readoutTimer = (message, politeness = "polite", timeout = 5000) => {
        return setTimeout(() => {
            srReadout(message, politeness);
        }, timeout);
    };

    // Inactive handler
    const inactivityHandler = (e) => {
        const counterMessage = replaceInString(srCounterMessage, {
            maxLength,
            currentLength: validElements.textArea.value.length,
            remaining: maxLength - validElements.textArea.value.length,
        });
        if (e.type === "focusin") {
            srReadout(counterMessage, "polite");
        } else if (e.type === "focusout") {
            inactivityTimer && clearTimeout(inactivityTimer);
        }
        return;
    };

    const updateCharacterCount = () => {
        renderState(views.textArea.validated, element);
        const charCount = validElements.textArea.value.length;
        currentSpan.innerHTML = charCount;

        // Check for custom counter label
        const counterMessage = replaceInString(srCounterMessage, {
            maxLength,
            currentLength: charCount,
            remaining: maxLength - charCount,
        });
        // Reset focusin timer
        inactivityTimer && clearTimeout(inactivityTimer);
        inactivityTimer = readoutTimer(counterMessage);

        //A11y checking if character count is over 90% of maxlength
        if (charCount > maxLength * 0.9 && charCount <= maxLength) {
            srReadout(counterMessage, "assertive");
        } else if (charCount > maxLength) {
            maxlengthTimer && clearTimeout(maxlengthTimer);
            maxlengthTimer = readoutTimer(counterMessage, "assertive", 500);
        } else {
            srReadout("", "polite");
        }

        if (charCount > maxLength) {
            validElements.textArea.setCustomValidity("Exceeded max length");
            renderState(views.textArea.invalid, validElements.textArea);
            renderState(views.counterSpan.invalid, counterSpan);
        } else {
            validElements.textArea.setCustomValidity("");
            renderState(views.textArea.valid, validElements.textArea);
            renderState(views.counterSpan.valid, counterSpan);
        }
    };

    const buildCounterSpan = () => {
        if (!maxLength || maxLength < 1) {
            console.warn(
                `${componentName} requires attribute data-maxlength to be set to have character count and be more than 0`
            );
            return;
        }

        const maxSpan = createElement("span", {
            html: "/" + maxLength,
        });
        currentSpan = createElement("span", {
            html: "0",
            class: componentClasses.counterCurrent,
        });
        counterSpan = createElement("span", {
            class: componentClasses.counter,
        });
        counterSpan.appendChild(currentSpan);
        counterSpan.appendChild(maxSpan);
        validElements.header.appendChild(counterSpan);
        validElements.textArea.addEventListener("input", updateCharacterCount);

        // Build sr only span
        const srID = `${componentName}-sr-${randomNumGenerator()}`;
        srText = createElement("span", {
            id: srID,
            class: componentClasses.sr,
            role: "log",
        });
        // Check for custom label
        srCounterMessage = counterSpan.dataset.srLabel
            ? counterSpan.dataset.srLabel
            : mergedProps.srLabel
            ? mergedProps.srLabel
            : "{currentLength}/{maxLength}";

        validElements.textArea.addEventListener("focusin", inactivityHandler);
        validElements.textArea.addEventListener("focusout", inactivityHandler);
        counterSpan.appendChild(srText);
    };

    const dispose = function () {
        validElements.textArea.removeEventListener("input", updateCharacterCount);
        validElements.textArea.removeEventListener("focusin", inactivityHandler);
        validElements.textArea.removeEventListener("focusout", inactivityHandler);
        clearTimeout(inactivityTimer);
        clearTimeout(maxlengthTimer);
        element[componentName] = null;
        element = null;
    };

    if (mergedProps.counter) {
        buildCounterSpan();
    }

    injectErrorIcon();

    const api = {
        element,
        dispose,
    };

    element[componentName] = api;
    // Return ourselves incase we are captured in js.
    return api;
}
